import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight,
  Users, Target, FileBarChart, Coins,
} from "lucide-react";
import useReveal from "@/hooks/useReveal";
import BlogTicker from "@/components/BlogTicker";

const DIFFERENTIATORS = [
  { n: "01", k: "Niente teoria", v: "Ogni raccomandazione è implementabile la settimana stessa. Se non lo è, non te la diciamo." },
  { n: "02", k: "Affiancamento reale", v: "I partner sono operativi sul progetto. Non gestiamo da remoto attraverso junior." },
  { n: "03", k: "Risultati misurabili", v: "Definiamo a inizio percorso i numeri che cambiano. E li difendiamo trimestre dopo trimestre." },
];

const PROBLEMS = [
  {
    p: "Crescita bloccata, fatturato che oscilla",
    s: "Mappiamo bottleneck operativi e leve commerciali. Definiamo obiettivi misurabili a 90 / 180 / 365 giorni.",
  },
  {
    p: "Decisioni prese a istinto, senza dati",
    s: "Costruiamo un cruscotto di KPI essenziali. Ogni scelta strategica diventa tracciabile e revisionabile.",
  },
  {
    p: "Liquidità tirata, investimenti rimandati",
    s: "Identifichiamo i bandi e gli strumenti di finanza agevolata pertinenti. Gestiamo l'iter, non solo la consulenza.",
  },
];

const SERVICES = [
  {
    n: "01",
    icon: Users,
    title: "Consulenza Aziendale",
    payoff: "La tua crescita, il nostro obiettivo.",
    summary:
      "Affianchiamo la tua azienda con soluzioni su misura per migliorare le performance, ottimizzare i processi e affrontare le sfide del mercato.",
    href: "/servizi#consulenza-aziendale",
  },
  {
    n: "02",
    icon: Target,
    title: "Pianificazione Strategica",
    payoff: "Strategie chiare. Risultati concreti.",
    summary:
      "Definiamo insieme obiettivi chiari e strategie vincenti per guidare la tua azienda verso una crescita sostenibile e duratura.",
    href: "/servizi#pianificazione-strategica",
  },
  {
    n: "03",
    icon: FileBarChart,
    title: "Business Plan su Misura",
    payoff: "La tua idea. Il nostro piano. Il tuo successo.",
    summary:
      "Trasformiamo la tua idea in un progetto concreto con un business plan professionale, chiaro e convincente, pronto per banche, investitori e partner.",
    href: "/servizi#business-plan",
  },
  {
    n: "04",
    icon: Coins,
    title: "Finanza Agevolata",
    payoff: "Capitale per crescere. Senza diluire l'equity.",
    summary:
      "Ti aiutiamo ad accedere a bandi, contributi e finanziamenti per sostenere i tuoi progetti e far crescere la tua azienda.",
    href: "/servizi#finanza-agevolata",
  },
];

const APPROACH = [
  { n: "01", title: "Analisi", body: "Audit numerico e operativo. Tre settimane per leggere davvero la tua azienda." },
  { n: "02", title: "Strategia", body: "Definiamo priorità, obiettivi e KPI. Scartiamo tutto ciò che non sposta il business." },
  { n: "03", title: "Implementazione", body: "Lavoriamo dentro l'azienda, fianco a fianco con il team. Non consegnamo PDF: portiamo a casa progetti." },
  { n: "04", title: "Monitoraggio", body: "Revisione trimestrale dei numeri. Correggiamo la rotta finché i risultati non sono nei tempi previsti." },
];

export default function Home() {
  useReveal();
  return (
    <div data-testid="page-home">
      {/* SEZIONE 1 — Blog ticker */}
      <BlogTicker />

      {/* SEZIONE 2 — HERO */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="bv-container pt-20 md:pt-28 pb-20 md:pb-32">
          <div className="grid grid-cols-12 gap-8 items-end">
            {/* Colonna sinistra (60%) */}
            <div className="col-span-12 lg:col-span-7 reveal">
              <p className="overline" data-testid="hero-overline">
                BV — Strategia · Visione · Crescita
              </p>
              <h1
                data-testid="hero-headline"
                className="h-display text-[40px] sm:text-[60px] md:text-[80px] lg:text-[96px] mt-8 max-w-[16ch] leading-[0.95]"
              >
                Strategia, visione<br />
                e <span style={{ color: "#2BA8AD" }}>crescita</span><br />
                al servizio delle imprese.
              </h1>
              <p
                data-testid="hero-subheadline"
                className="mt-10 max-w-[60ch] text-[17px] md:text-[19px] leading-relaxed text-[#0E2E4D]/80"
              >
                Soluzioni concrete, strategie su misura e finanza agevolata per far crescere
                il tuo business e raggiungere nuovi traguardi. Affianchiamo imprenditori
                e PMI italiane con un metodo strutturato.
              </p>

              <div className="mt-12 flex flex-wrap items-center gap-4">
                <Link
                  to="/contatti"
                  data-testid="hero-cta-primary"
                  className="inline-flex items-center gap-3 bg-[#0E2E4D] text-white px-7 py-5 text-[12px] tracking-[0.20em] uppercase font-display font-bold hover:bg-[#2BA8AD] transition-colors"
                >
                  Richiedi una consulenza <ArrowRight size={16} />
                </Link>
                <Link
                  to="/servizi"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-2 py-3 text-[12px] tracking-[0.20em] uppercase font-display font-bold link-underline text-[#0E2E4D]"
                >
                  Scopri i servizi <ArrowRight size={14} />
                </Link>
              </div>
            </div>

            {/* Colonna destra (40%) — case study card */}
            <div className="block col-span-12 lg:col-span-5 reveal mt-10 lg:mt-0" style={{ "--reveal-delay": "120ms" }}>
              <div className="aspect-[4/5] border border-[#0E2E4D]/15 overflow-hidden bv-network relative">
                <img
                  src="/home-hero.jpg"
                  alt="Strategia e crescita BV Business Consulting"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SEZIONE 3 — NON UN'ALTRA SOCIETÀ DI CONSULENZA */}
      <section className="bg-[#ECE7DC]" data-testid="why-bv-section">
        <div className="bv-container py-24 md:py-32">
          <div className="grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 md:col-span-5 reveal">
              <p className="section-mark">/ Perché BV</p>
              <h2 className="h-display text-4xl md:text-6xl mt-6 leading-[0.95] max-w-[14ch]">
                Non un'altra<br />società di<br />consulenza.
              </h2>
              <p className="mt-8 text-[16px] text-[#0E2E4D]/70 max-w-md">
                Lavoriamo con un numero limitato di aziende all'anno. È l'unico modo per garantire
                il livello di affiancamento che promettiamo.
              </p>
            </div>
            <ul className="col-span-12 md:col-span-7 md:col-start-6 divide-y divide-[#0E2E4D]/20 border-t border-b border-[#0E2E4D]/20">
              {DIFFERENTIATORS.map((d, i) => (
                <li
                  key={d.k}
                  className="grid grid-cols-12 gap-6 py-10 reveal"
                  style={{ "--reveal-delay": `${i * 120}ms` }}
                  data-testid={`differentiator-${i}`}
                >
                  <span className="col-span-2 font-mono text-[12px] tracking-[0.22em] text-[#2BA8AD] pt-2">{d.n}</span>
                  <div className="col-span-10">
                    <h3 className="h-display text-2xl md:text-3xl text-[#0E2E4D]">{d.k}</h3>
                    <p className="mt-3 text-[15px] text-[#0E2E4D]/75 leading-relaxed max-w-prose">{d.v}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROBLEMA → SOLUZIONE */}
      <section className="bv-container py-28 md:py-40" data-testid="problem-solution-section">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5 reveal">
            <p className="section-mark">/ Problema → Soluzione</p>
            <h2 className="h-display text-4xl md:text-5xl mt-6 max-w-[18ch]">
              I tre nodi che bloccano la crescita di una <span style={{ color: "#2BA8AD" }}>PMI italiana</span>.
            </h2>
            <p className="mt-8 text-[16px] text-[#0E2E4D]/70 max-w-md">
              Vediamo gli stessi pattern in settori diversi. Cambiano i numeri, non i problemi.
              Ecco come interveniamo.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            <ul className="border-t border-[#0E2E4D]/15">
              {PROBLEMS.map((row, i) => (
                <li
                  key={i}
                  className="grid grid-cols-12 gap-6 py-10 border-b border-[#0E2E4D]/15 reveal"
                  style={{ "--reveal-delay": `${i * 100}ms` }}
                  data-testid={`problem-row-${i}`}
                >
                  <p className="col-span-12 md:col-span-5 font-display font-bold text-xl md:text-[22px] text-[#0E2E4D] leading-tight uppercase">
                    {row.p}
                  </p>
                  <p className="col-span-12 md:col-span-7 text-[15px] text-[#0E2E4D]/75 leading-relaxed">
                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#2BA8AD] mr-2">→ Soluzione</span>
                    {row.s}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SERVIZI */}
      <section className="bv-network text-white" data-testid="services-section">
        <div className="bv-container pt-28 md:pt-40 pb-24 md:pb-32">
          <div className="flex items-end justify-between flex-wrap gap-6 reveal">
            <div>
              <p className="section-mark" style={{ color: "rgba(255,255,255,0.6)" }}>/ Servizi</p>
              <h2 className="h-display text-4xl md:text-6xl mt-6 max-w-[20ch]">
                Quattro aree, un solo principio: <br className="hidden md:block" />
                <span style={{ color: "#36BFC4" }}>portare a casa il risultato</span>.
              </h2>
            </div>
            <Link
              to="/servizi"
              data-testid="services-cta-all"
              className="inline-flex items-center gap-2 text-[12px] tracking-[0.20em] uppercase font-display font-bold link-underline text-white"
            >
              Tutti i servizi <ArrowUpRight size={14} />
            </Link>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/15">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.n}
                  to={s.href}
                  data-testid={`service-card-${s.n}`}
                  className="group p-10 bg-[#0E2E4D] hover:bg-[#1A3A5F] transition-colors reveal"
                  style={{ "--reveal-delay": `${i * 110}ms` }}
                >
                  <div className="flex items-start gap-5">
                    <span className="bv-icon-ring" style={{ borderColor: "#36BFC4", color: "#36BFC4" }}>
                      <Icon size={22} strokeWidth={1.6} />
                    </span>
                    <div className="flex-1">
                      <p className="font-mono text-[11px] tracking-[0.22em] text-[#36BFC4]">{s.n}</p>
                      <h3 className="h-display text-2xl md:text-3xl mt-3">{s.title}</h3>
                      <p className="font-display text-[12px] tracking-[0.12em] uppercase text-[#36BFC4]/85 mt-3 font-semibold">
                        {s.payoff}
                      </p>
                      <p className="mt-5 text-[15px] text-white/80 leading-relaxed">{s.summary}</p>
                      <span className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-mono text-[#36BFC4] group-hover:translate-x-1 transition-transform">
                        Approfondisci <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROCCIO — rimossa dalla home su richiesta utente */}
    </div>
  );
}
